import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { io } from 'socket.io-client';
import Layout from './components/Layout';

// Intercepteur axios global : ajoute le token à toutes les requêtes
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Etudiants from './pages/Etudiants';
import CartesEtudiants from './pages/CartesEtudiants';
import Enseignants from './pages/Enseignants';
import Inscriptions from './pages/Inscriptions';
import Paiements from './pages/Paiements';
import Notes from './pages/Notes';
import Presences from './pages/Presences';
import FaireAppel from './pages/FaireAppel';
import Passation from './pages/Passation';
import EmploiTemps from './pages/EmploiTemps';
import EmploiDirection from './pages/EmploiDirection';
import SuiviSeances from './pages/SuiviSeances';
import SuiviAbsences from './pages/SuiviAbsences';
import Stages from './pages/Stages';
import Messages from './pages/Messages';
import Annonces from './pages/Annonces';
import EtudiantDashboard from './pages/EtudiantDashboard';
import Annees from './pages/Parametres/Annees';
import Filieres from './pages/Parametres/Filieres';
import Niveaux from './pages/Parametres/Niveaux';
import Classes from './pages/Parametres/Classes';
import Salles from './pages/Parametres/Salles';
import Periodes from './pages/Parametres/Periodes';
import Modules from './pages/Parametres/Modules';
import Matieres from './pages/Parametres/Matieres';
import Users from './pages/Parametres/Users';
import Roles from './pages/Parametres/Roles';
import ImportEtudiants from './pages/Parametres/ImportEtudiants';
import AuditLogs from './pages/Parametres/AuditLogs';
import Archive from './pages/Parametres/Archive';
import Backup from './pages/Parametres/Backup';
import NotificationPreferences from './pages/Parametres/NotificationPreferences';
import Personnel from './pages/Parametres/Personnel';
import Parametres from './pages/Parametres/Index';

const NotFound = () => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h2" sx={{ fontWeight: 700, color: '#ef4444', mb: 2 }}>404</Typography>
    <Typography variant="h5" sx={{ color: '#4b5563', mb: 1 }}>Page non trouvée</Typography>
    <Typography variant="body1" sx={{ color: '#9ca3af', mb: 3 }}>La page que vous cherchez n'existe pas ou a été déplacée.</Typography>
    <Button variant="contained" onClick={() => window.history.back()}>Retour</Button>
  </Box>
);

const PlaceholderPage = ({ title }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h4" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>{title}</Typography>
    <Typography variant="body1" sx={{ color: '#9ca3af' }}>Cette page est en cours de développement.</Typography>
  </Box>
);

const ProfilePage = () => <PlaceholderPage title="Mon Profil" />;
const SettingsPage = () => <PlaceholderPage title="Paramètres" />;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/dashboard" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'ENSEIGNANT', 'CONSULTANT', 'CHEF_FILIERE', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><Dashboard /></PrivateRoute>} />
      <Route path="/etudiants" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><Etudiants /></PrivateRoute>} />
      <Route path="/cartes-etudiants" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT', 'DIRECTION_GENERALE']}><CartesEtudiants /></PrivateRoute>} />
      <Route path="/enseignants" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE']}><Enseignants /></PrivateRoute>} />
      <Route path="/inscriptions" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE']}><Inscriptions /></PrivateRoute>} />
      <Route path="/paiements" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE']}><Paiements /></PrivateRoute>} />
      <Route path="/notes" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'CHEF_FILIERE', 'ENSEIGNANT', 'ETUDIANT', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><Notes /></PrivateRoute>} />
      <Route path="/presences" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'DIRECTION_GENERALE']}><Presences /></PrivateRoute>} />
      <Route path="/faire-appel" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'ENSEIGNANT', 'SCOLARITE', 'RESPONSABLE_SCOLARITE']}><FaireAppel /></PrivateRoute>} />
      <Route path="/passation" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR']}><Passation /></PrivateRoute>} />
      <Route path="/emploi-temps" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'ENSEIGNANT', 'ETUDIANT', 'CHEF_FILIERE', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><EmploiTemps /></PrivateRoute>} />
      <Route path="/emploi-direction" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CHEF_FILIERE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><EmploiDirection /></PrivateRoute>} />
      <Route path="/suivi-seances" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'ENSEIGNANT', 'CHEF_FILIERE', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><SuiviSeances /></PrivateRoute>} />
      <Route path="/suivi-absences" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CHEF_FILIERE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE']}><SuiviAbsences /></PrivateRoute>} />
      <Route path="/stages" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'CHEF_FILIERE', 'ETUDIANT', 'DIRECTION_GENERALE']}><Stages /></PrivateRoute>} />
      <Route path="/messages" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'ENSEIGNANT', 'ETUDIANT', 'CHEF_FILIERE', 'DIRECTION_GENERALE']}><Messages /></PrivateRoute>} />
      <Route path="/annonces" element={<PrivateRoute allowedRoles={['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'ETUDIANT', 'CONSULTANT', 'CHEF_FILIERE', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE']}><Annonces /></PrivateRoute>} />
      <Route path="/etudiant/dashboard" element={<PrivateRoute allowedRoles={['ETUDIANT']}><EtudiantDashboard /></PrivateRoute>} />
      <Route path="/parametres" element={<PrivateRoute allowedRoles={['ADMIN']}><Parametres /></PrivateRoute>} />
      <Route path="/parametres/annees" element={<PrivateRoute allowedRoles={['ADMIN']}><Annees /></PrivateRoute>} />
      <Route path="/parametres/filieres" element={<PrivateRoute allowedRoles={['ADMIN']}><Filieres /></PrivateRoute>} />
      <Route path="/parametres/niveaux" element={<PrivateRoute allowedRoles={['ADMIN']}><Niveaux /></PrivateRoute>} />
      <Route path="/parametres/classes" element={<PrivateRoute allowedRoles={['ADMIN']}><Classes /></PrivateRoute>} />
      <Route path="/parametres/salles" element={<PrivateRoute allowedRoles={['ADMIN']}><Salles /></PrivateRoute>} />
      <Route path="/parametres/periodes" element={<PrivateRoute allowedRoles={['ADMIN']}><Periodes /></PrivateRoute>} />
      <Route path="/parametres/modules" element={<PrivateRoute allowedRoles={['ADMIN']}><Modules /></PrivateRoute>} />
      <Route path="/parametres/matieres" element={<PrivateRoute allowedRoles={['ADMIN']}><Matieres /></PrivateRoute>} />
      <Route path="/parametres/users" element={<PrivateRoute allowedRoles={['ADMIN']}><Users /></PrivateRoute>} />
      <Route path="/parametres/roles" element={<PrivateRoute allowedRoles={['ADMIN']}><Roles /></PrivateRoute>} />
      <Route path="/parametres/users/import-etudiants" element={<PrivateRoute allowedRoles={['ADMIN']}><ImportEtudiants /></PrivateRoute>} />
      <Route path="/parametres/audit-logs" element={<PrivateRoute allowedRoles={['ADMIN']}><AuditLogs /></PrivateRoute>} />
      <Route path="/parametres/archive" element={<PrivateRoute allowedRoles={['ADMIN']}><Archive /></PrivateRoute>} />
      <Route path="/parametres/backup" element={<PrivateRoute allowedRoles={['ADMIN']}><Backup /></PrivateRoute>} />
      <Route path="/parametres/notifications" element={<PrivateRoute><NotificationPreferences /></PrivateRoute>} />
      <Route path="/parametres/personnel" element={<PrivateRoute allowedRoles={['ADMIN']}><Personnel /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    if (user) { setLoading(false); return; }
    setLoading(false);
  }, [user]);

  // Connexion Socket.IO
  useEffect(() => {
    if (user) {
      if (!socketRef.current) {
        socketRef.current = io({
          transports: ['websocket', 'polling']
        });
        socketRef.current.on('connect', () => {
          console.log('✅ Socket.IO connecté:', socketRef.current.id);
          socketRef.current.emit('join_user', user._id);
        });
      }
    } else {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user]);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />} />
        <Route path="/*" element={
          user ? (
            <Layout user={user} onLogout={handleLogout} socket={socketRef.current}>
              <AppRoutes />
            </Layout>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
      </Routes>
    </Router>
  );
}

export default App;


